import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLE = {
  published: { bg: '#eff4ff', color: '#3a6ad9', label: 'Open for bids' },
  bidding:   { bg: '#f0fde4', color: '#111111', label: 'Bidding' },
  under_loi: { bg: '#fffde4', color: '#111111', label: 'Under LOI' },
};

const BID_STATUS_STYLE = {
  submitted:    { bg: '#f3f4f6', color: '#374151', label: 'Bid submitted' },
  loi_pending:  { bg: '#fffde4', color: '#111111', label: 'LOI pending' },
  loi_signed:   { bg: '#eff4ff', color: '#3a6ad9', label: 'LOI signed' },
  dd_open:      { bg: '#f0fde4', color: '#111111', label: 'DD room open' },
};

function fmtUpb(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

function deadlineText(deadline) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return { text: 'Bidding closed', urgent: true };
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (days >= 7) return { text: `Closes ${new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, urgent: false };
  if (days >= 1) return { text: `Closes in ${days}d ${hours}h`, urgent: true };
  return { text: `Closes in ${hours}h ${minutes}m`, urgent: true };
}

export default function MarketPoolsList() {
  const { token, investor } = useInvestorAuth();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/market/pools`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        setPools(res.data.pools || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load pools.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const isApproved = investor?.status === 'approved';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <Header />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px 64px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Available Pools</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
              Active DoorlyMarket loan pools open for bidding.
            </p>
          </div>
        </div>

        {!isApproved && (
          <div style={{ backgroundColor: '#ffe6e9', border: '1px solid #ffd6db', borderRadius: 12, padding: '14px 18px', marginBottom: 18, color: '#ff082f', fontSize: 13 }}>
            Your account is pending approval. You can preview available pools below,
            but bidding requires approval. We'll email you the moment your account is reviewed.
          </div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: '#ff082f', backgroundColor: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 12 }}>{error}</div>
        ) : pools.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            No pools are currently published. Check back soon — you'll be emailed when new pools go live.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {pools.map((p) => <PoolCard key={p.id} pool={p} approved={isApproved} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function PoolCard({ pool, approved }) {
  const statusInfo = STATUS_STYLE[pool.status] || { bg: '#f3f4f6', color: '#374151', label: pool.status };
  const dl = deadlineText(pool.bidDeadline);
  const myBid = pool.myBid;
  const bidInfo = myBid ? BID_STATUS_STYLE[myBid.status] : null;

  return (
    <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{pool.poolName}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{pool.poolNumber}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 9999, backgroundColor: statusInfo.bg, color: statusInfo.color, fontSize: 12, fontWeight: 700 }}>
            {statusInfo.label}
          </span>
          {bidInfo && (
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 9999, backgroundColor: bidInfo.bg, color: bidInfo.color, fontSize: 12, fontWeight: 700 }}>
              {bidInfo.label}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
        <Metric label="Loans" value={pool.loanCount || 0} />
        <Metric label="WA FICO" value={pool.waFico || '—'} />
        <Metric label="WA Rate" value={pool.waNoteRate ? `${Number(pool.waNoteRate).toFixed(2)}%` : '—'} />
        <Metric label="WA LTV" value={pool.waOriginalLtv ? `${Number(pool.waOriginalLtv).toFixed(0)}%` : '—'} />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 10,
        backgroundColor: pool.isRedacted ? '#f9fafb' : '#fff',
        border: '1px solid #f3f4f6',
        position: 'relative',
        marginBottom: 14,
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total UPB</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
            {pool.isRedacted ? <span style={{ color: '#9ca3af', fontSize: 14, fontWeight: 600 }}>Available after approval</span> : fmtUpb(pool.totalUpb)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Asking Price</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
            {pool.isRedacted
              ? <span style={{ color: '#9ca3af', fontSize: 14, fontWeight: 600 }}>—</span>
              : (pool.askingPricePct ? `${Number(pool.askingPricePct).toFixed(2)}%` : '—')}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: dl?.urgent ? '#ff082f' : '#6b7280', fontWeight: dl?.urgent ? 700 : 400 }}>
          {dl ? dl.text : 'No deadline set'}
        </div>
        <Link
          to={`/market/pools/${pool.id}`}
          style={{
            display: 'inline-block',
            backgroundColor: '#3a6ad9',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 100,
            fontSize: 13,
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          {approved ? 'View Pool →' : 'Preview Pool →'}
        </Link>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Header() {
  const { investor, investorLogout } = useInvestorAuth();
  return (
    <header style={{ backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Link to="/market/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
        <img src="/doorly-market-logo.png" alt="Doorly Market" style={{ height: '32px', width: 'auto', display: 'block' }} />
      </Link>
      <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
        <Link to="/market/dashboard" style={navLink}>Dashboard</Link>
        <Link to="/market/pools" style={{ ...navLink, color: '#3a6ad9' }}>Pools</Link>
        <Link to="/market/bids" style={navLink}>My Bids</Link>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {investor?.firstName} {investor?.lastName}
        </span>
        <button onClick={investorLogout} style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
          Sign out
        </button>
      </nav>
    </header>
  );
}

const navLink = { fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' };
